import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { NO_COMPARISON, variation } from "../../utils/analyticsDeltas";
import { toSegments } from "../../utils/analyticsLabels";
import { todayIn } from "../../utils/analyticsPeriod";
import { percentOf, rateChange } from "../../utils/analyticsSeries";
import { roundedShare } from "../../utils/analyticsSentences";
import ChartFrame from "../charts/ChartFrame";
import ResponsiveChart from "../charts/ResponsiveChart";
import StackedBar, { STACKED_BAR_HEIGHT } from "../charts/StackedBar";
import { LOCALES, formatCount, formatPercent, formatShare } from "../charts/format";
import EvolutionCard from "./EvolutionCard";
import KpiStrip from "./KpiStrip";
import { countText } from "./kpis";
import Section from "./Section";

// A seccao "Sessao iniciada" do separador Conversas (RPAA-286, item 10, D11): a frase de resumo, as conversas
// com sessao em percentagem e os clientes distintos com sessao, os tres estados (desde o inicio, durante a
// conversa, anonimas) e a evolucao da percentagem. Quem a chama decide se aparece (`sessionsShown`).
// `customers` e o corpo de /signed-in-customers, que a pagina pede ao abrir o separador: sem ele nem erro o
// pedido vem a caminho.
const SIGNED_IN_METRICS = [
  { key: "signed_in_share", label: "analyticsSignedInShare", better: "up", rate: { part: "conversations_signed_in", whole: "conversations_by_session" } },
];

const card = { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" };
// O valor a espera da rota, como nos blocos do CSAT e da atividade: ocupado para os leitores de ecra e com o texto escondido.
const pending = (translate) => (
  <span aria-busy="true">
    <span className="sr-only">{translate("loading")}</span>
    <span className="inline-block h-8 w-16 rounded animate-pulse align-middle" style={{ backgroundColor: "var(--color-surface-raised)" }} />
  </span>
);

const SessionsStrip = ({ summary, customers, customersError, onRetry, previousComplete }) => {
  const { translate, language } = useLanguage();
  const { session } = summary.sessions;
  const m = summary.series.metrics;
  const signedIn = session.values.signed_in || 0;
  const conversationsKpi = {
    key: "signed_in_conversations",
    label: translate("analyticsSignedInConversations"),
    value: session.total ? formatPercent((100 * signedIn) / session.total, language) : translate("analyticsNotAvailable"),
    change: rateChange({ series: summary.series, part: "conversations_signed_in", whole: "conversations_by_session", better: "up", previousComplete, language }),
    spark: [percentOf(m.conversations_signed_in.current, m.conversations_by_session.current), percentOf(m.conversations_signed_in.previous, m.conversations_by_session.previous)],
    context: countText(signedIn, "analyticsKpiConversationCount", translate, language),
  };
  const customersKpi = { key: "signed_in_customers", label: translate("analyticsSignedInCustomers"), info: translate("analyticsSignedInCustomersNote") };
  if (customersError) {
    customersKpi.value = translate("analyticsNotAvailable");
    customersKpi.context = (
      <span role="alert" className="flex flex-wrap items-center gap-2" style={{ color: "var(--color-error-text)" }}>
        <span>{apiErrorMessage(customersError, translate("analyticsLoadFailed"))}</span>
        <button type="button" onClick={onRetry} className="px-3 py-1.5 text-sm rounded-lg border" style={{ ...card, color: "var(--color-text-primary)" }}>
          {translate("analyticsRetry")}
        </button>
      </span>
    );
  } else if (!customers) {
    customersKpi.value = pending(translate);
  } else {
    const { value, previous } = customers.customers;
    customersKpi.value = formatCount(value, language);
    // O `previous` vem a null quando o anterior comeca antes do horizonte da retencao: comparava um periodo cortado.
    customersKpi.change = previousComplete ? variation({ kind: "count", value, previous }, language) : NO_COMPARISON;
    const oneDay = customers.retention_days === 1;
    customersKpi.context = customers.limited_by_retention
      ? oneDay
        ? translate("analyticsSignedInCustomersRetentionOneDay")
        : translate("analyticsSignedInCustomersRetention", { days: customers.retention_days })
      : previous === null
        ? oneDay
          ? translate("analyticsSignedInCustomersNoPreviousOneDay")
          : translate("analyticsSignedInCustomersNoPrevious", { days: customers.retention_days })
        : translate("analyticsKpiPrevious", { previous: formatCount(previous, language) });
  }
  return <KpiStrip label={translate("analyticsSignedInNumbers")} kpis={[conversationsKpi, customersKpi]} />;
};

// Os tres estados, no modelo do CompositionCard, em percentagem das conversas do registo (o total do
// `sign_in`). As conversas de antes do registo so tem "com sessao" e "sem" e ficam fora: a nota diz
// quantas sao e o dia a partir do qual ha os tres. Sem nenhuma hora do registo no periodo o `sign_in_since`
// vem a null, e a nota nao tem data.
const SignInCard = ({ sessions, zone }) => {
  const { translate, language } = useLanguage();
  const { session, sign_in: signIn, sign_in_since: since } = sessions;
  const segments = toSegments(signIn, "sign_in", translate);
  const share = (value) => formatShare(value, signIn.total, language);
  const before = session.total - signIn.total;
  const day = since && new Date(`${todayIn(zone, new Date(since))}T00:00:00Z`).toLocaleDateString(LOCALES[language] || language, { dateStyle: "medium", timeZone: "UTC" });
  const note =
    before <= 0
      ? null
      : !since
        ? translate("analyticsSignInBeforeAll")
        : before === 1
          ? translate("analyticsSignInBeforeOne", { date: day })
          : translate("analyticsSignInBefore", { count: formatCount(before, language), date: day });
  const table = {
    columns: [
      { key: "label", label: translate("analyticsCategory") },
      { key: "value", label: translate("analyticsCount"), numeric: true, format: share },
    ],
    rows: segments,
  };
  return (
    <ChartFrame title={translate("analyticsSignInTitle")} info={translate("analyticsSignInNote")} note={note} table={table} empty={signIn.total === 0}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {segments.map((segment) => (
          <div key={segment.key} className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
              <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: segment.color }} />
              {segment.label}
            </p>
            <p className="text-2xl font-semibold tabular-nums" style={{ color: "var(--color-text-primary)" }}>
              {formatPercent((100 * segment.value) / signIn.total, language)}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {countText(segment.value, "analyticsKpiConversationCount", translate, language)}
            </p>
          </div>
        ))}
      </div>
      <ResponsiveChart height={STACKED_BAR_HEIGHT}>
        {(width) => <StackedBar segments={segments} width={width} label={translate("analyticsSignInTitle")} formatValue={share} />}
      </ResponsiveChart>
    </ChartFrame>
  );
};

const SessionsBlock = ({ summary, customers, customersError, onRetry, previousComplete, period, today }) => {
  const { translate } = useLanguage();
  const { session } = summary.sessions;
  const share = roundedShare(session.values.signed_in || 0, session.total);
  return (
    <Section title={translate("analyticsSignedIn")} className="grid gap-4 lg:grid-cols-2 items-start">
      {share !== null && (
        <p className="text-sm lg:col-span-2" style={{ color: "var(--color-text-primary)" }}>
          {translate("analyticsSessionsLead", { share })}
        </p>
      )}
      <div className="lg:col-span-2">
        <SessionsStrip summary={summary} customers={customers} customersError={customersError} onRetry={onRetry} previousComplete={previousComplete} />
      </div>
      <SignInCard sessions={summary.sessions} zone={period.tz} />
      <EvolutionCard
        series={summary.series}
        metrics={SIGNED_IN_METRICS}
        title={translate("analyticsSignedInEvolution")}
        partialLast={period.to === today}
        previousComplete={previousComplete}
        cut={summary.cut}
        timeZone={period.tz}
      />
    </Section>
  );
};

export default SessionsBlock;
